import { AllMeetingStatus, AttendState, MeetingConflict, MeetingRecord, NewOrderSignStatus } from '../../../../type/meeting';

export type ScheduleAction = 'join' | 'leave' | 'refuse' | 'signIn' | 'extend' | 'end' | 'cancel' | 'revoke' | 'summary' | 'rebook';
export type ScheduleTagTone = 'orange' | 'blue' | 'gray' | 'red';

export const SCHEDULE_ACTION_LABELS: Record<ScheduleAction, string> = {
  join: '参加',
  leave: '请假',
  refuse: '拒绝',
  signIn: '会议签到',
  extend: '延长会议',
  end: '提前结束',
  cancel: '取消会议',
  revoke: '撤销申请',
  summary: '查看纪要',
  rebook: '再次预约'
};

type SchedulePresentation = {
  tags: { text: string; tone: ScheduleTagTone }[];
  invitation: { text: string; tone: ScheduleTagTone; actions: ScheduleAction[] } | null;
  primaryAction: ScheduleAction | null;
  moreActions: ScheduleAction[];
  conflict: boolean;
  signed: boolean;
};

/** 按移动端的操作集合生成菜单，再排除右上角主按钮或邀请下拉已有的操作。 */
export const getSchedulePresentation = (record: MeetingRecord, isCreator: boolean, now = Date.now()): SchedulePresentation => {
  const { meeting, inviteState, summaryState, hasModified } = record;
  const result: SchedulePresentation = {
    tags: [],
    invitation: null,
    primaryAction: null,
    moreActions: [],
    conflict: false,
    signed: false
  };
  const addTag = (text: string, tone: ScheduleTagTone) => result.tags.push({ text, tone });
  const canceled = meeting.status === AllMeetingStatus.CANCEL || inviteState === AttendState.INVITE_WITHDRAW;
  const ended = meeting.status === AllMeetingStatus.FINISH || meeting.status === AllMeetingStatus.FILED || now >= meeting.endTime;
  const during = meeting.status === AllMeetingStatus.DURING || (meeting.status === AllMeetingStatus.BEFORE && now >= meeting.beginTime);
  const before = meeting.status === AllMeetingStatus.BEFORE && !during;
  const soon = before && meeting.beginTime >= now && meeting.beginTime - now <= 10 * 60 * 1000;
  const joined = inviteState === AttendState.JOINED;

  if (canceled) {
    addTag('已取消', 'gray');
  } else if (meeting.status === AllMeetingStatus.AUDITING) {
    addTag('审批中', 'orange');
    if (isCreator) result.moreActions.push('revoke');
  } else if (meeting.status === AllMeetingStatus.AUDIT_REFUSE) {
    addTag('不通过', 'orange');
  } else if (meeting.status === AllMeetingStatus.AUDIT_REVOKE) {
    addTag('已撤销', 'orange');
  } else if (meeting.status === AllMeetingStatus.AUDIT_EXPIRED) {
    addTag('已过期', 'orange');
  } else if (ended) {
    if (inviteState === AttendState.TO_BE_CONFIRMED) addTag('未处理', 'gray');
    if (joined && record.hasSummary === true) {
      addTag('有纪要', 'blue');
    }
    if (joined && summaryState === 1) {
      result.moreActions.push('summary');
    }
  } else if (before || during) {
    if (during) addTag('会议中', 'blue');
    if (!isCreator) {
      if (inviteState === AttendState.TO_BE_CONFIRMED) {
        result.invitation = { text: '新邀请', tone: 'orange', actions: ['join', 'leave', 'refuse'] };
        result.moreActions.push('join');
        if (before) result.moreActions.push('leave');
        result.moreActions.push('refuse');
      } else if (inviteState === AttendState.ASK_FOR_LEAVE) {
        result.invitation = { text: '已请假', tone: 'orange', actions: ['join', 'refuse'] };
        result.moreActions.push('join', 'refuse');
      } else if (inviteState === AttendState.REFUSED) {
        result.invitation = { text: '已拒绝', tone: 'gray', actions: ['join', 'leave'] };
        result.moreActions.push('join', 'leave');
      } else if (joined) {
        result.moreActions.push('leave', 'refuse');
      }
    }

    if (joined || isCreator) {
      const hasMeetingRoom = Boolean(meeting.roomOrderList?.length);
      result.signed = hasMeetingRoom && meeting.meetingSignView?.signStatus === NewOrderSignStatus.FINISH_SIGN;
      const showSignIn = hasMeetingRoom && !result.signed;
      if (during) {
        if (showSignIn) result.primaryAction = 'signIn';
        if (isCreator) {
          if (!showSignIn) result.primaryAction = 'extend';
          else result.moreActions.push('extend');
          result.moreActions.push('end');
        }
      } else {
        if (soon) {
          addTag('即将开始', 'orange');
          if (showSignIn) result.primaryAction = 'signIn';
        }
        if (isCreator) {
          if (soon) result.moreActions.push('extend');
          result.moreActions.push('cancel');
        }
      }
    }
  }

  // 与 h5-client 一致：附加提示独立叠加，不覆盖会议状态，也不影响操作权限。
  if (hasModified) addTag('有变更', 'blue');
  result.conflict = record.conflict === MeetingConflict.YES && inviteState !== AttendState.REFUSED && inviteState !== AttendState.ASK_FOR_LEAVE;
  if (result.conflict) addTag('有冲突', 'red');

  result.moreActions.push('rebook');
  const exposedActions = new Set(result.invitation?.actions || []);
  if (result.primaryAction) exposedActions.add(result.primaryAction);
  result.moreActions = result.moreActions.filter((action) => !exposedActions.has(action));
  return result;
};
